/**
 * Data Transformers for Catalog Management
 *
 * Transforms API response data (snake_case) to frontend types (camelCase).
 */
import {
  APITabCounts,
  APISubTabCounts,
  APIProduct,
  APISourceAttribute,
  APIAttributeValueObject,
  APIUnmappedProduct,
  APIPagination,
  APIUnmappedPagination,
} from '../types/api.types';
import {
  TabCounts,
  SKUMatchedRow,
  ProductAttribute,
  ValueStatus,
  IgnoredProduct,
  IgnoredUpdate,
  ProcessedProduct,
  UnmappedAttributeRow,
} from '../types';
import { TabId, MPNUPCSubTabId } from '../types/tabs.types';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Format internal tab name to readable display name
 * e.g., "sku_matched" → "SKU Matched", "mpn_upc_matched" → "MPN/UPC Matched"
 */
const formatTabName = (tabName: string): string => {
  const TAB_DISPLAY_NAMES: Record<string, string> = {
    'sku_matched': 'SKU Matched',
    'mpn_upc_matched': 'MPN/UPC Matched',
    'unmatched': 'Unmatched',
    'ignored_products': 'Ignored Products',
    'ignored_updates': 'Ignored Updates',
    'processed': 'Processed',
  };
  return TAB_DISPLAY_NAMES[tabName] || tabName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// ============================================================================
// Tab Count Transformers
// ============================================================================

/**
 * Transform API tab counts to frontend TabCounts format
 */
export function transformTabCounts(
  tabs: APITabCounts,
  subTabs: APISubTabCounts,
  missingMappingCount: number = 0
): TabCounts {
  return {
    skuMatched: tabs.sku_matched,
    mpnUpcMatched: tabs.mpn_upc_matched,
    unmatched: tabs.unmatched,
    ignoredProducts: tabs.ignored_products,
    ignoredUpdates: tabs.ignored_updates,
    processed: tabs.processed,
    missingMapping: missingMappingCount,
  };
}

/**
 * Transform sub-tab counts for MPN/UPC Matched view
 */
export function transformSubTabCounts(subTabs: APISubTabCounts): {
  unmappedSku: number;
  unmappedSkuWithUpdates: number;
} {
  return {
    unmappedSku: subTabs["mpn_upc_matched/unmapped_sku"],
    unmappedSkuWithUpdates: subTabs["mpn_upc_matched/unmapped_sku_with_updates"],
  };
}

// ============================================================================
// Tab Name Mappers
// ============================================================================

/**
 * Map frontend TabId to API tab name
 */
export function mapTabIdToApiTab(tabId: TabId): string {
  const mapping: Record<TabId, string> = {
    'sku-matched': 'sku_matched',
    'mpn-upc-matched': 'mpn_upc_matched',
    'unmatched': 'unmatched',
    'ignored-products': 'ignored_products',
    'ignored-updates': 'ignored_updates',
    'processed': 'processed',
  };
  return mapping[tabId];
}

/**
 * Map frontend MPNUPCSubTabId to API sub_tab name
 */
export function mapSubTabIdToApiSubTab(subTabId: MPNUPCSubTabId): string {
  const mapping: Record<MPNUPCSubTabId, string> = {
    'unmapped-sku': 'unmapped_sku',
    'unmapped-sku-with-updates': 'unmapped_sku_with_updates',
  };
  return mapping[subTabId];
}

// ============================================================================
// Product Transformers
// ============================================================================

/**
 * Helper to extract value from a field that can be ValueObject or plain string
 */
function extractFieldValue(field: any): string {
  if (field == null) return '';
  // If it's an object with a 'value' property (ValueObject)
  if (typeof field === 'object' && 'value' in field) {
    return field.value != null ? String(field.value) : '';
  }
  // If it's a plain string
  if (typeof field === 'string') {
    return field;
  }
  // Fallback: try to stringify
  return String(field);
}

/**
 * Extract is_updated flag from a field (ValueObject or plain value)
 */
function extractFieldIsUpdated(field: any): boolean {
  if (field == null) return false;
  if (typeof field === 'object' && 'is_updated' in field) {
    return !!field.is_updated;
  }
  return false;
}

/**
 * Transform API product to SKUMatchedRow format
 */
export function transformToSKUMatchedRow(product: APIProduct): SKUMatchedRow {
  const sourceData = product.source_data || {};
  const prod = sourceData.prod || {};
  const matchResult = product.match_result;
  const existingProduct = matchResult?.existing_product;

  // Get attribute count - can be array length, object with count property, or dict of attributes
  const getAttributeCount = (attrs: any): number => {
    if (!attrs) return 0;
    if (Array.isArray(attrs)) return attrs.length;
    if (typeof attrs === 'object') {
      // Check if it has a count property
      if ('count' in attrs) return attrs.count || 0;
      // Otherwise it's a dict of attributes - count the keys
      return Object.keys(attrs).length;
    }
    return 0;
  };

  return {
    id: product._id, // String ID from API
    matchedGmsId: matchResult?.matched_gms_id,
    catalog: {
      product: existingProduct?.name || '',
      category: matchResult?.category || '',
      attributeCount: getAttributeCount(matchResult?.attributes),
      description: existingProduct?.description != null ? String(existingProduct.description) : existingProduct?.name,
      sku: existingProduct?.sku,
      price: existingProduct?.price != null ? String(existingProduct.price) : undefined,
      upc: existingProduct?.upc,
      modelNumber: existingProduct?.model_number,
      pls: existingProduct?.product_lifecycle_status,
      parentId: existingProduct?.parent_id,
      version: existingProduct?.version,
      realSku: existingProduct?.real_sku,
    },
    imported: {
      product: extractFieldValue(prod.name),
      // Change-highlight flags (13765 follow-up): the BE stamps is_updated on
      // source_data.prod.name / source_data.category, but only updatedFields
      // (footer metadata) ever read the flags — so a name/category change
      // counted in the Update badge rendered with no yellow highlight.
      productIsUpdated: extractFieldIsUpdated(prod.name),
      categoryIsUpdated: extractFieldIsUpdated(sourceData.category) || extractFieldIsUpdated(prod.category),
      // category is at source_data.category, not source_data.prod.category
      category: extractFieldValue(sourceData.category) || extractFieldValue(prod.category),
      // category_id for create product API - extract string value as it may be a ValueObject
      categoryId: extractFieldValue(prod.category_id) || extractFieldValue(sourceData.category_id),
      // total counts are at top level of product
      totalUpdatesCount: product.total_updates_count || 0,
      totalNewCount: product.total_new_count || 0,
      totalRemovedCount: product.total_removed_count || 0,
      // description is at source_data.prod.description (ValueObject)
      description: extractFieldValue(prod.description),
      // sku is at product.sku (top level), not source_data.prod.sku
      sku: product.sku || extractFieldValue(prod.sku),
      price: extractFieldValue(prod.price),
      upc: extractFieldValue(prod.upc),
      modelNumber: extractFieldValue(prod.model_number),
      pls: extractFieldValue(prod.product_lifecycle_status),
      // parent_id and real_sku are ValueObjects at source_data level, fallback to top-level
      parentId: extractFieldValue(sourceData.parent_id) || product.parent_id || '',
      version: extractFieldValue(prod.version),
      realSku: extractFieldValue(sourceData.real_sku) || product.real_sku || '',
      updatedFields: {
        description: extractFieldIsUpdated(prod.description),
        price: extractFieldIsUpdated(prod.price),
        upc: extractFieldIsUpdated(prod.upc),
        modelNumber: extractFieldIsUpdated(prod.model_number),
        pls: extractFieldIsUpdated(prod.product_lifecycle_status),
        parentId: extractFieldIsUpdated(sourceData.parent_id),
        version: extractFieldIsUpdated(prod.version),
        realSku: extractFieldIsUpdated(sourceData.real_sku),
      },
    },
    attributes: transformSourceAttributes(sourceData.attributes),
    catalogAttributes: transformMatchResultAttributes(
      matchResult?.attributes as Record<string, string[]> | undefined,
      sourceData.attributes
    ),
    isExpanded: false,
    isSelected: false,
  };
}

/**
 * Check if a value is an APIAttributeValueObject ({status, value})
 */
function isAttributeValueObject(val: any): val is APIAttributeValueObject {
  return typeof val === 'object' && val !== null && 'status' in val && 'value' in val;
}

/**
 * Extract string value from an attribute value that could be string or object
 */
function extractAttributeValue(val: any): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'object' && val !== null) {
    // Handle {status, value} objects from real API
    if ('value' in val) return String(val.value || '');
    // Handle {name: "..."} objects
    if ('name' in val) return String(val.name || '');
    // Try to stringify
    return JSON.stringify(val);
  }
  return String(val);
}

/**
 * Extract per-value status from attribute values
 * Returns parallel array of ValueStatus for each value
 */
function extractValueStatuses(values: (APIAttributeValueObject | string)[]): ValueStatus[] {
  return values.map((val) => {
    if (isAttributeValueObject(val)) {
      return val.status as ValueStatus;
    }
    return 'unchanged';
  });
}

/**
 * Transform API source attributes to ProductAttribute array
 */
export function transformSourceAttributes(
  attributes?: APISourceAttribute[]
): ProductAttribute[] {
  if (!attributes || !Array.isArray(attributes)) {
    return [];
  }
  return attributes.map((attr) => {
    // Handle values that could be strings or {status, value} objects
    let valueStr = '';
    if (Array.isArray(attr.values)) {
      valueStr = attr.values.map(extractAttributeValue).filter(Boolean).join(', ');
    } else if (attr.values) {
      valueStr = extractAttributeValue(attr.values);
    }

    // Preserve individual values for multi-badge rendering
    const individualValues = Array.isArray(attr.values)
      ? attr.values.map(extractAttributeValue).filter(Boolean)
      : valueStr ? [valueStr] : [];

    // Extract per-value statuses for badge coloring
    const valueStatuses = Array.isArray(attr.values)
      ? extractValueStatuses(attr.values)
      : [];

    // Derive isUpdated: true if any value has 'added' or 'removed' status
    const hasChanges = valueStatuses.some((s) => s === 'added' || s === 'removed');
    const isUpdated = attr.is_updated ?? hasChanges;

    return {
      name: attr.name || '',
      value: valueStr,
      values: individualValues,
      valueStatuses,
      isNew: attr.is_new || false,
      isUpdated,
      isRemoved: attr.is_removed || false,
      isCustom: attr.is_custom === true,
    };
  });
}

/**
 * Transform match_result.attributes (Record<string, string[]>) to ProductAttribute array
 * Used for the catalog side of the expanded row
 */
export function transformMatchResultAttributes(
  matchAttributes?: Record<string, string[]>,
  sourceAttributes?: APISourceAttribute[]
): ProductAttribute[] {
  if (!matchAttributes) return [];

  // AB#13810: the API carries is_custom on source_data.attributes only; the catalog
  // side is keyed by the same names, so mirror the flag across so both columns tag.
  const customNames = new Set<string>();
  // Use source attribute order for consistent display, excluding is_new attributes
  const orderedNames: string[] = [];
  if (sourceAttributes) {
    for (const attr of sourceAttributes) {
      if (attr.is_custom === true) customNames.add(attr.name);
      if (!attr.is_new && matchAttributes[attr.name]) {
        orderedNames.push(attr.name);
      }
    }
  }

  // Add any remaining attributes from matchAttributes not in source
  for (const name of Object.keys(matchAttributes)) {
    if (!orderedNames.includes(name)) {
      orderedNames.push(name);
    }
  }

  return orderedNames.map((name) => {
    const values = matchAttributes[name] || [];
    return {
      name,
      value: values.join(', '),
      values,
      // Catalog side: no statuses, no new/updated flags
      isCustom: customNames.has(name),
    };
  });
}

/**
 * Transform API product to IgnoredProduct format
 */
export function transformToIgnoredProduct(product: APIProduct): IgnoredProduct {
  const sourceData = product.source_data || {};
  const prod = sourceData.prod || {};

  return {
    id: product._id, // String ID from API
    // sku is at product.sku (top level), not source_data.prod.sku
    sku: product.sku || extractFieldValue(prod.sku),
    name: extractFieldValue(prod.name),
    // category is at source_data.category, not source_data.prod.category
    category: extractFieldValue(sourceData.category) || extractFieldValue(prod.category),
    // Use original_tab (saved when product was ignored) for "moved from" display
    movedFrom: formatTabName(product.original_tab || product.tab || 'Unknown'),
    comment: product.ignore_reason || '',
    attributes: transformSourceAttributes(sourceData.attributes),
    description: extractFieldValue(prod.description),
    price: extractFieldValue(prod.price),
    upc: extractFieldValue(prod.upc),
    modelNumber: extractFieldValue(prod.model_number),
    pls: extractFieldValue(prod.product_lifecycle_status),
    parentId: extractFieldValue(sourceData.parent_id),
    version: extractFieldValue(prod.version),
    realSku: extractFieldValue(sourceData.real_sku),
    reportingCategory: extractFieldValue(sourceData.reporting_category),
  };
}

/**
 * Transform API product to IgnoredUpdate format
 */
export function transformToIgnoredUpdate(product: APIProduct): IgnoredUpdate {
  const sourceData = product.source_data || {};
  const prod = sourceData.prod || {};

  return {
    id: product._id, // String ID from API
    name: extractFieldValue(prod.name),
    // category is at source_data.category, not source_data.prod.category
    category: extractFieldValue(sourceData.category) || extractFieldValue(prod.category),
    movedFrom: formatTabName(product.original_tab || product.tab || 'Unknown'),
    // total counts are at top level
    totalUpdatesCount: product.total_updates_count || 0,
    totalNewCount: product.total_new_count || 0,
    totalRemovedCount: product.total_removed_count || 0,
    attributes: transformSourceAttributes(sourceData.attributes),
    sku: product.sku || extractFieldValue(prod.sku),
    description: extractFieldValue(prod.description),
    price: extractFieldValue(prod.price),
    upc: extractFieldValue(prod.upc),
    modelNumber: extractFieldValue(prod.model_number),
    pls: extractFieldValue(prod.product_lifecycle_status),
    parentId: extractFieldValue(sourceData.parent_id),
    version: extractFieldValue(prod.version),
    realSku: extractFieldValue(sourceData.real_sku),
    reportingCategory: extractFieldValue(sourceData.reporting_category),
  };
}

/**
 * Transform API product to ProcessedProduct format
 */
export function transformToProcessedProduct(product: APIProduct): ProcessedProduct {
  const sourceData = product.source_data || {};
  const prod = sourceData.prod || {};

  // reviewed_by is an object with { userid, username }
  const reviewedBy = product.reviewed_by;
  let processedByName = 'Unknown';
  if (reviewedBy) {
    if (typeof reviewedBy === 'object' && reviewedBy.username) {
      processedByName = reviewedBy.username;
    } else if (typeof reviewedBy === 'string') {
      processedByName = reviewedBy;
    }
  }

  // processed_at can be Unix timestamp (number) or ISO string
  let processedOnDate = '';
  if (product.processed_at) {
    if (typeof product.processed_at === 'number') {
      processedOnDate = formatTimestamp(product.processed_at);
    } else {
      processedOnDate = formatDate(product.processed_at);
    }
  }

  return {
    id: product._id, // String ID from API
    name: extractFieldValue(prod.name),
    // category is at source_data.category, not source_data.prod.category
    category: extractFieldValue(sourceData.category) || extractFieldValue(prod.category),
    // Use API field names: action_taken for new product check
    isNewProduct: product.action_taken === 'create_new' || false,
    processedBy: processedByName,
    processedOn: processedOnDate,
    attributes: transformSourceAttributes(sourceData.attributes),
    sku: product.sku || extractFieldValue(prod.sku),
    description: extractFieldValue(prod.description),
    price: extractFieldValue(prod.price),
    upc: extractFieldValue(prod.upc),
    modelNumber: extractFieldValue(prod.model_number),
    pls: extractFieldValue(prod.product_lifecycle_status),
    parentId: extractFieldValue(sourceData.parent_id),
    version: extractFieldValue(prod.version),
    realSku: extractFieldValue(sourceData.real_sku),
    reportingCategory: extractFieldValue(sourceData.reporting_category),
  };
}

/**
 * Transform API unmapped product to UnmappedAttributeRow format
 */
export function transformToUnmappedRow(product: APIUnmappedProduct): UnmappedAttributeRow {
  // Collect all unmapped attribute values from nested structure
  const unmappedAttributeValues: string[] = [];
  product.attributes_with_unmapped_values?.forEach((attr) => {
    unmappedAttributeValues.push(...attr.unmapped_values);
  });

  return {
    id: product._id, // String ID from API
    name: product.prod?.name || '',
    category: product.mapped_category || '',
    unmappedAttributeCount: product.unmapped_attributes_count,
    unmappedAttributeValueCount: product.unmapped_attribute_values_count,
    unmappedAttributes: product.unmapped_attributes || [],
    unmappedAttributeValues: unmappedAttributeValues,
    isExpanded: false,
    isMappingSelected: false,
    // Product metadata
    sku: product.sku || '',
    description: product.prod?.description || '',
    modelNumber: product.prod?.model_number || '',
    price: product.prod?.price || '',
    upc: product.prod?.upc || '',
    version: product.prod?.version || '',
    status: product.prod?.product_lifecycle_status || '',
    realSku: product.real_sku || '',
    parentId: product.parent_id || '',
    unmappedCategory: product.unmapped_category || '',
    reportingCategory: product.reporting_category || '',
  };
}

// ============================================================================
// Pagination Transformers
// ============================================================================

export interface PaginationInfo {
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

/**
 * Default pagination values when API doesn't return pagination
 */
const DEFAULT_PAGINATION: PaginationInfo = {
  page: 1,
  perPage: 10,
  total: 0,
  totalPages: 0,
};

/**
 * Transform API pagination to frontend format
 * Returns default values if pagination is undefined
 */
export function transformPagination(pagination?: APIPagination): PaginationInfo {
  if (!pagination) {
    return DEFAULT_PAGINATION;
  }
  return {
    page: pagination.page ?? 1,
    perPage: pagination.per_page ?? 10,
    total: pagination.total ?? 0,
    totalPages: pagination.total_pages ?? 0,
  };
}

/**
 * Transform unmapped products API pagination to frontend format
 * Returns default values if pagination is undefined
 */
export function transformUnmappedPagination(
  pagination?: APIUnmappedPagination
): PaginationInfo {
  if (!pagination) {
    return DEFAULT_PAGINATION;
  }
  return {
    page: pagination.page ?? 1,
    perPage: pagination.limit ?? 10,
    total: pagination.total ?? 0,
    totalPages: pagination.total_pages ?? 0,
  };
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format ISO date string to display format (e.g., "28 Nov 2025")
 */
export function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return isoDate;
  }
}

/**
 * Format Unix timestamp (seconds) to display format (e.g., "28 Nov 2025")
 */
export function formatTimestamp(timestamp: number): string {
  try {
    // Unix timestamp is in seconds, JS Date expects milliseconds
    const date = new Date(timestamp * 1000);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return String(timestamp);
  }
}

/**
 * Transform a list of API products to frontend row format based on tab
 */
export function transformProducts<T>(
  products: APIProduct[],
  tabId: TabId
): T[] {
  switch (tabId) {
    case 'sku-matched':
    case 'mpn-upc-matched':
    case 'unmatched':
      return products.map(transformToSKUMatchedRow) as T[];
    case 'ignored-products':
      return products.map(transformToIgnoredProduct) as T[];
    case 'ignored-updates':
      return products.map(transformToIgnoredUpdate) as T[];
    case 'processed':
      return products.map(transformToProcessedProduct) as T[];
    default:
      return [];
  }
}

/**
 * Transform unmapped products list
 */
export function transformUnmappedProducts(
  products: APIUnmappedProduct[]
): UnmappedAttributeRow[] {
  return products.map(transformToUnmappedRow);
}
